import { MapBoxGlobeMapStyles } from '../../../utils/mapHelpers';
import { CircleLayerSpecification, FillLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from 'mapbox-gl';

export const lineLayerStyle = (mapStyle: string, focusedVoyageId: number): LineLayerSpecification => {
    return {
        id: 'lines',
        source: 'lines',
        type: 'line',
        paint: {
            //'line-color': ['get', 'color'],
            // 'line-color': [
            //     'case',
            //     ['==', ['get', 'signalActivity'], 'Under Way'],
            //     '#1E503E',
            //     ['==', ['get', 'portActivity'], 'Stopped (at anchor)'],
            //     '#DF6721',
            //     ['==', ['get', 'portActivity'], 'Stopped (moored)'],
            //     '#F0D035',
            //     '#819391',
            // ],
            'line-color': mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#00355f' : '#ffffff',
            //"line-dasharray": [3, 3],
            'line-width': [
                'case',
                ['==', ['get', 'voyageId'], focusedVoyageId], // Check if the current voyage_id matches the focused one
                3,
                1 // Default width for all other lines
            ],
            'line-emissive-strength': 1 // Make color glow (important for Globe)
        },
    }
};

export const lineLayerStyleForDailyPositions = (mapStyle: string): LineLayerSpecification => {
    return {
        id: 'line-layer-style-for-daily-positions',
        source: 'vesselDailyPositionsAsLine',
        type: 'line',
        paint: {
            //'line-color': ['get', 'color'],
            // 'line-color': [
            //     'case',
            //     ['==', ['get', 'signalActivity'], 'Under Way'],
            //     '#1E503E',
            //     ['==', ['get', 'portActivity'], 'Stopped (at anchor)'],
            //     '#DF6721',
            //     ['==', ['get', 'portActivity'], 'Stopped (moored)'],
            //     '#F0D035',
            //     '#819391',
            // ],
            'line-color': mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#00355f' : '#ffffff',
            "line-dasharray": [2, 2],
            'line-width': 1,
            'line-emissive-strength': 1 // Make color glow (important for Globe)
        },
    }
};

export const arrowLayerStyle = (): SymbolLayerSpecification => {
    return {
        id: 'arrows',
        source: 'lines',
        type: 'symbol',
        layout: {
            'symbol-placement': 'line', // Places symbols along the line
            'symbol-spacing': 50, // Distance between arrows
            'icon-image': 'arrow-icon', // Reference your arrow icon
            'icon-size': 2,
            //'icon-rotate': ['get', 'bearings'], // Optional: rotates based on line direction

            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        },
    };
};

export const stoppagesLayerStyle = (mapStyle: string,): CircleLayerSpecification => {
    return {
        id: 'stoppages',
        source: 'stoppages',
        type: 'circle',
        paint: {
            'circle-radius': 4,
            //'circle-color': ['get', 'color'], // Gets color from the layer data source
            'circle-color': mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#00355f' : '#ffffff',
            //'circle-stroke-color': '#ffffff',
            //'circle-stroke-width': 1,
            'circle-emissive-strength': 1 // Make color glow (important for Globe)
        },
    }
};

export const portCallsLayerStyle = (): CircleLayerSpecification => {
    const circleMin = 10;
    const circleMax = 40;
    const portDaysMin = 0.5;
    const portDaysMax = 20;

    return {
        id: 'port-calls',
        source: 'portCalls',
        type: 'circle',
        paint: {
            'circle-radius': [
                'min',
                circleMax,
                [
                    'max',
                    circleMin,
                    [
                        '+',
                        circleMin,
                        [
                            '*',
                            [
                                '-',
                                circleMax,
                                circleMin
                            ],
                            [
                                '/',
                                [
                                    '-',
                                    ['get', 'totalTimeInPort'],
                                    portDaysMin
                                ],
                                portDaysMax - portDaysMin
                            ]
                        ]
                    ]
                ]
            ],
            'circle-color': [
                'case',
                ['==', ['get', 'portActivity'], 'Load'],
                '#569FD3',
                ['==', ['get', 'portActivity'], 'Discharge'],
                '#DF6721',
                ['==', ['get', 'portActivity'], 'In Shipyard'],
                '#F0D035',
                ['==', ['get', 'portActivity'], 'In Shipbreaker'],
                '#F0D035',
                '#819391',
            ], // Gets color from the layer data source
            //'circle-stroke-color': '#ffffff',
            //'circle-stroke-width': 1,
            'circle-opacity': 0.5,
            'circle-emissive-strength': 1 // Make color glow (important for Globe)
        },
    }
};

export const pointLayerStyle = (zoomLevel: number): CircleLayerSpecification => {
    return {
        id: 'points',
        source: 'points',
        type: 'circle',
        paint: {
            'circle-radius': zoomLevel * 1.15,
            // 'circle-color': [
            // 	'case',
            // 	['<', ['get', 'speed'], 1],
            // 	'#facc15', // Color when 'speed' > 1
            // 	'#0f944c', // Default color if none of the conditions match
            // ],
            'circle-color': ['get', 'color'],
            'circle-emissive-strength': 1 // Make color glow (important for Globe)
        },
    }
};

export const pointArrowLayerStyle = (zoomLevel: number, colorKey: string): SymbolLayerSpecification => {
    return {
        id: `vessel-arrows-${colorKey}`,
        source: `point-${colorKey}`,
        type: 'symbol',
        filter: ['!', ['has', 'point_count']], // only render unclustered points
        layout: {
            'icon-image': ['concat', 'arrow-', ['get', 'color']], // Dynamically use the arrow icon with color
            'icon-size': zoomLevel * 0.08,
            'icon-rotate': ['get', 'heading'],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        },
    };
};

// Cluster represented as a vessel arrow icon (same shape as individual vessels).
// Slightly larger icon-size than individual arrows so the cluster is visually distinguishable.
export const clusterArrowLayerStyle = (zoomLevel: number, colorKey: string): SymbolLayerSpecification => {
    return {
        id: `vessel-cluster-arrows-${colorKey}`,
        source: `point-${colorKey}`,
        type: 'symbol',
        filter: ['has', 'point_count'],
        layout: {
            'icon-image': ['concat', 'arrow-', ['get', 'color']],
            'icon-size': zoomLevel * 0.10, // slightly larger than individual (0.08) to hint at cluster
            'icon-rotate': ['get', 'heading'],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        },
    };
};

export const pointArrowLayerStyleForVoyage = (focusedVoyageId: number): SymbolLayerSpecification => {
    return {
        id: 'vessel-arrows-for-voyage',
        source: 'voyageDailySignals',
        type: 'symbol',
        layout: {
            'icon-image': 'arrow-icon',
            //'icon-size': .1,
            'icon-size': [
                'case',
                ['==', ['get', 'voyageId'], focusedVoyageId], // Check if the current voyage_id matches the focused one
                .16, // Increase the width (e.g., 3 for focused)
                .1 // Default width for all other lines
            ],
            'icon-rotate': ['get', 'heading'],
            'icon-allow-overlap': true,
            //'icon-ignore-placement': true,
        },
    };
};

export const pointArrowLayerStyleForOngoingVoyage = (focusedVoyageId: number): SymbolLayerSpecification => {
    return {
        id: 'vessel-arrows-for-ongoing-voyage',
        source: 'vesselOngoingVoyageDailySignals',
        type: 'symbol',
        layout: {
            'icon-image': 'arrow-icon',
            'icon-size': [
                'case',
                ['==', ['get', 'voyageId'], focusedVoyageId], // Check if the current voyage_id matches the focused one
                .16, // Increase the width (e.g., 3 for focused)
                .1 // Default width for all other lines
            ],
            'icon-rotate': ['get', 'heading'],
            'icon-allow-overlap': true,
            //'icon-ignore-placement': true,
        },
    };
};

export const pointArrowLayerStyleForDailyPostitons = (): SymbolLayerSpecification => {
    return {
        id: 'vessel-arrows-for-daily-positions',
        source: 'vesselDailyPositions',
        type: 'symbol',
        layout: {
            'icon-image': 'arrow-icon-long',
            //'icon-size': .09,
            'icon-size': .1,
            // 'icon-size': [
            //     'case',
            //     ['==', ['get', 'voyageId'], focusedVoyageId], // Check if the current voyage_id matches the focused one
            //     .16, // Increase the width (e.g., 3 for focused)
            //     .1 // Default width for all other lines
            // ],
            'icon-rotate': ['get', 'heading'],
            'icon-allow-overlap': true,
        },
    };
};

// export const polygonLayerStyle: FillLayerSpecification = {
//     id: 'polygons',
//     source: 'polygons',
//     type: 'fill',
//     paint: {
//         'fill-color': '#2B5E97',
//         'fill-opacity': 0.75,
//         // Make color glow (important for Globe)
//         'fill-emissive-strength': 1,
//     },
// };

export const polygonLayerStyle = (mapStyle: string,): FillLayerSpecification => {
    return {
        id: 'polygons',
        source: 'polygons',
        type: 'fill',
        paint: {
            'fill-color': mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#2B5E97' : '#3E7DB5',
            'fill-opacity': 0.75,
            // Make color glow (important for Globe)
            'fill-emissive-strength': 1,
            'fill-outline-color': mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#000000' : '#ffffff',
        },
    }
};